import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Music, ChevronLeft, ChevronRight, ExternalLink, Clock, Loader2, AlertCircle, Mail, RefreshCw, CheckCircle2, XCircle, Settings, Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PC_CACHE_PREFIX = 'pc_schedules_v3_';
const PC_SCHEDULE_URL = 'https://services.planningcenteronline.com/schedule';

// R42 (2026-09-07): the cache used to live until local midnight with no way to
// bypass it, so a roster/set-list change in Planning Center was invisible
// until the next day. Now: 30 minutes (still never past midnight), a manual
// Refresh button, and a silent refetch when the tab regains focus after expiry.
const PC_CACHE_TTL_MS = 30 * 60 * 1000;
const PC_REFRESH_COOLDOWN_MS = 30 * 1000; // one widget load is 11–16 Planning Center calls

function getCachedSchedules(userId) {
  try {
    const raw = localStorage.getItem(PC_CACHE_PREFIX + userId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (new Date(parsed.expires) < new Date()) {
      localStorage.removeItem(PC_CACHE_PREFIX + userId);
      return null;
    }
    return { ...parsed.data, fetched_at: parsed.fetchedAt || null };
  } catch { return null; }
}

function setCachedSchedules(userId, data, fetchedAt = null) {
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  const ttl = new Date(now.getTime() + PC_CACHE_TTL_MS);
  const expires = ttl < midnight ? ttl : midnight;
  localStorage.setItem(PC_CACHE_PREFIX + userId, JSON.stringify({ data, expires: expires.toISOString(), fetchedAt: fetchedAt || now.toISOString() }));
}

function formatUpdatedAt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const sameDay = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return sameDay ? `Updated ${time}` : `Updated ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${time}`;
}

function formatName(fullName) {
  const parts = (fullName || '').trim().split(' ');
  if (parts.length < 2) return fullName;
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

function StatusIcon({ status }) {
  if (status === 'confirmed') return <CheckCircle2 className="w-[18px] h-[18px] text-emerald-500 flex-shrink-0" />;
  if (status === 'declined') return <XCircle className="w-[18px] h-[18px] text-red-400 flex-shrink-0" />;
  return <Clock className="w-[18px] h-[18px] text-amber-400 flex-shrink-0" />;
}

function TeamSettings({ userId, pinnedTeamIds, onSave, organizations = [], selectedOrg = null, onSelectOrg }) {
  const [teams, setTeams] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set(pinnedTeamIds || []));
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  // Sync selected state when pinned teams change (after save + refetch)
  useEffect(() => {
    setSelected(new Set(pinnedTeamIds || []));
  }, [pinnedTeamIds]);

  const fetchTeams = async () => {
    if (teams) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/planning-center/teams/${userId}`);
      if (res.ok) {
        const data = await res.json();
        setTeams(data.service_types || []);
      }
    } catch (err) {
      console.error('Error fetching PCO teams:', err);
    } finally { setLoading(false); }
  };

  const handleToggle = (teamId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(teamId)) next.delete(teamId);
      else next.add(teamId);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/planning-center/pinned-teams/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ team_ids: [...selected] })
      });
      if (res.ok) {
        onSave([...selected]);
        setOpen(false);
      }
    } catch (err) {
      console.error('Error saving pinned teams:', err);
    } finally { setSaving(false); }
  };

  const hasChanges = (() => {
    const current = new Set(pinnedTeamIds || []);
    if (current.size !== selected.size) return true;
    for (const id of selected) { if (!current.has(id)) return true; }
    return false;
  })();

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) fetchTeams(); }}>
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 dark:text-slate-400 hover:text-teal-500 hover:bg-slate-50 dark:hover:bg-white/5 transition-all"
                data-testid="pc-settings-btn"
              >
                <Settings className="w-4 h-4" />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="bg-slate-800 text-white px-3 py-1.5 rounded-lg text-xs font-medium">
            <p>Show/Hide Teams</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent align="end" className="w-72 p-0" data-testid="pc-teams-popover">
        {organizations.length > 1 && (
          <div className="p-3 border-b border-slate-100 dark:border-white/10" data-testid="pc-org-picker">
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Your Church</p>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 mb-1.5">
              Your Planning Center login is linked to multiple churches. Pick which one shows here.
            </p>
            {organizations.map(org => (
              <label
                key={org}
                className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 cursor-pointer"
                data-testid={`pc-org-option-${org.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <Checkbox
                  checked={selectedOrg === org}
                  onCheckedChange={(checked) => onSelectOrg?.(checked ? org : null)}
                />
                <span className="text-sm text-slate-700 dark:text-slate-200 truncate">{org}</span>
              </label>
            ))}
            {!selectedOrg && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400 px-2 mt-1">Currently showing services from all churches.</p>
            )}
          </div>
        )}
        <div className="p-3 border-b border-slate-100 dark:border-white/10">
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Show Teams on Dashboard</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Select teams to show only those. Leave all unchecked to show the teams you're scheduled on.</p>
        </div>
        <div className="max-h-64 overflow-y-auto p-2">
          {loading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
            </div>
          )}
          {!loading && teams && teams.length === 0 && (
            <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-4">No teams found</p>
          )}
          {!loading && teams && teams.length > 1 && (
            <div
              className="flex items-start gap-2 mx-1 mb-2 px-2.5 py-2 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-100 dark:border-amber-500/20"
              data-testid="pc-teams-multiservice-note"
            >
              <Info className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] leading-snug text-amber-700 dark:text-amber-300">
                <span className="font-semibold">Each service type is separate.</span> For example, "Band" under <span className="italic">Sunday</span> and "Band" under <span className="italic">Wednesday</span> are different teams — if you check Band only under Sunday, your Wednesday service may show <span className="font-semibold">no one</span>. To see Band in both, check it under <span className="font-semibold">each</span> service.
              </p>
            </div>
          )}
          {!loading && teams && teams.map(st => (
            <div key={st.service_type_id} className="mb-2">
              <p className="text-[11px] font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider px-2 py-1">{st.service_type_name}</p>
              {st.teams.map(t => (
                <label
                  key={t.team_id}
                  className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 cursor-pointer"
                  data-testid={`pc-team-${t.team_id}`}
                >
                  <Checkbox
                    checked={selected.has(t.team_id)}
                    onCheckedChange={() => handleToggle(t.team_id)}
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-200">{t.name}</span>
                </label>
              ))}
            </div>
          ))}
        </div>
        {!loading && teams && teams.length > 0 && (
          <div className="p-2 border-t border-slate-100 dark:border-white/10">
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="w-full text-sm font-medium py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-teal-500 hover:bg-teal-600 text-white"
              data-testid="pc-teams-save-btn"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function PlanningCenterWidget({ userId, onReconnect, onQuickMessage }) {
  const [schedules, setSchedules] = useState([]);
  const [pinnedTeamIds, setPinnedTeamIds] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);       // silent refetch: card stays up
  const [refreshError, setRefreshError] = useState(null);    // silent refetch failed: keep the stale plans, say so
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [, setCooldownTick] = useState(0);
  const hasPlansRef = useRef(false);
  const lastFetchedAtRef = useRef(null);   // mirrors lastFetchedAt inside the stable callback
  const inFlightRef = useRef(false);

  /**
   * force: bypass the local cache. silent: keep the current card visible while
   * refetching (no spinner takeover). rebuildTeams: ask the server to rebuild
   * the pinned service-type map too (`?refresh=1`).
   */
  const fetchSchedules = useCallback(async ({ force = false, silent = false, rebuildTeams = false } = {}) => {
    if (!userId) { setLoading(false); return; }
    if (force) {
      localStorage.removeItem(PC_CACHE_PREFIX + userId);
    } else {
      const cached = getCachedSchedules(userId);
      if (cached && cached.schedules) {
        // Silent refetch with nothing new: leave state alone (a fresh array
        // from JSON.parse would reset the carousel to the first plan).
        if (silent && cached.fetched_at && cached.fetched_at === lastFetchedAtRef.current) { setLoading(false); return; }
        setSchedules(cached.schedules || []);
        setPinnedTeamIds(cached.pinned_team_ids || []);
        setOrganizations(cached.organizations || []);
        setSelectedOrg(cached.selected_organization || null);
        setLastFetchedAt(cached.fetched_at || null);
        lastFetchedAtRef.current = cached.fetched_at || null;
        setLoading(false);
        return;
      }
    }
    if (silent && inFlightRef.current) return; // a load is already running; last writer would win
    inFlightRef.current = true;
    const keepCard = silent && hasPlansRef.current;
    if (keepCard) setRefreshing(true); else setLoading(true);
    setRefreshError(null);
    if (!keepCard) setError(null);
    try {
      const response = await fetch(`${API_URL}/api/planning-center/schedule/${userId}${rebuildTeams ? '?refresh=1' : ''}`);
      if (response.status === 401) {
        if (keepCard) setRefreshError('reconnect'); else setError('reconnect');
        return;
      }
      if (response.status === 503 || response.status === 429) {
        if (keepCard) setRefreshError('rate'); else setError('rate');
        return;
      }
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      const plans = data.schedules || (data.schedule ? [data.schedule] : []);
      const fetchedAt = new Date().toISOString();
      setSchedules(plans);
      setPinnedTeamIds(data.pinned_team_ids || []);
      setOrganizations(data.organizations || []);
      setSelectedOrg(data.selected_organization || null);
      setLastFetchedAt(fetchedAt);
      lastFetchedAtRef.current = fetchedAt;
      setError(null);
      if (data.partial) {
        // Some Planning Center calls were rate-limited: show what came back,
        // say so, and do not cache it as fresh.
        setRefreshError('rate');
      } else {
        // Cache empty results too (bounded by the 30-minute TTL) so an empty
        // widget does not re-walk Planning Center on every tab return.
        setCachedSchedules(userId, {
          schedules: plans,
          pinned_team_ids: data.pinned_team_ids || [],
          organizations: data.organizations || [],
          selected_organization: data.selected_organization || null,
        }, fetchedAt);
      }
    } catch (err) {
      console.error('PC widget fetch error:', err);
      if (keepCard) setRefreshError('fetch'); else setError('fetch');
    } finally { inFlightRef.current = false; setLoading(false); setRefreshing(false); }
  }, [userId]);

  useEffect(() => { fetchSchedules(); }, [fetchSchedules]);
  useEffect(() => { setCurrentIndex(0); hasPlansRef.current = schedules.length > 0; }, [schedules]);

  // Refresh when the tab comes back and the cache has expired (cache-first, so
  // a still-valid cache costs no Planning Center calls).
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) fetchSchedules({ silent: true }); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchSchedules]);

  const cooldownActive = Date.now() < cooldownUntil;
  const handleManualRefresh = () => {
    if (refreshing || loading || cooldownActive) return;
    const until = Date.now() + PC_REFRESH_COOLDOWN_MS;
    setCooldownUntil(until);
    setTimeout(() => setCooldownTick(t => t + 1), PC_REFRESH_COOLDOWN_MS + 50); // re-enable the button
    fetchSchedules({ force: true, silent: true, rebuildTeams: true });
  };

  const handlePinnedSave = (newPinnedIds) => {
    setPinnedTeamIds(newPinnedIds);
    // Bypass the cache so the next fetch uses the new pinned teams
    fetchSchedules({ force: true });
  };

  const handleSelectOrg = async (orgName) => {
    setSelectedOrg(orgName);
    try {
      await fetch(`${API_URL}/api/planning-center/selected-org/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organization_name: orgName })
      });
    } catch (err) { console.error('Error saving selected org:', err); }
    fetchSchedules({ force: true });
  };

  const updatedLabel = formatUpdatedAt(lastFetchedAt);
  const refreshButton = (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleManualRefresh}
            disabled={refreshing || loading || cooldownActive}
            aria-label="Refresh from Planning Center"
            className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 dark:text-slate-400 hover:text-teal-500 hover:bg-slate-50 dark:hover:bg-white/5 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="pc-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="bg-slate-800 text-white px-3 py-1.5 rounded-lg text-xs font-medium">
          <p>{cooldownActive && !refreshing ? 'Just refreshed — try again in a moment' : 'Refresh from Planning Center'}</p>
          {updatedLabel && <p className="text-slate-300 font-normal">{updatedLabel}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const plan = schedules[currentIndex] || null;
  const totalPlans = schedules.length;
  const isFirst = currentIndex === 0;
  const isLast = currentIndex >= totalPlans - 1;

  // ── Loading ──
  if (loading) {
    return (
      <Card className="shadow-lg shadow-slate-200/50 border border-slate-100 dark:border-white/10 bg-card" data-testid="pc-widget">
        <CardContent className="p-5 flex items-center justify-center min-h-[200px]">
          <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
        </CardContent>
      </Card>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <Card className="shadow-lg shadow-slate-200/50 border border-slate-100 dark:border-white/10 bg-card" data-testid="pc-widget">
        <CardContent className="p-5 flex flex-col items-center justify-center min-h-[200px] gap-3">
          <AlertCircle className="w-6 h-6 text-slate-300" />
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {error === 'reconnect' ? 'Planning Center session expired'
              : error === 'rate' ? 'Planning Center is rate-limiting requests — try again in a minute'
              : 'Could not load schedule'}
          </p>
          <button
            onClick={error === 'reconnect' ? onReconnect : () => fetchSchedules({ force: true })}
            className="text-xs font-medium bg-slate-100 dark:bg-[#3a3d41] hover:bg-slate-200 text-slate-600 dark:text-slate-300 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5"
          >
            <RefreshCw className="w-3 h-3" />
            {error === 'reconnect' ? 'Reconnect' : 'Retry'}
          </button>
        </CardContent>
      </Card>
    );
  }

  // ── Empty ──
  if (!plan) {
    return (
      <Card className="shadow-lg shadow-slate-200/50 border border-slate-100 dark:border-white/10 bg-card" data-testid="pc-widget">
        <CardContent className="p-5 flex flex-col items-center justify-center min-h-[200px] gap-2 relative">
          <div className="absolute top-3 right-3 flex items-center gap-0.5">
            {refreshButton}
            <TeamSettings
              userId={userId}
              pinnedTeamIds={pinnedTeamIds}
              onSave={handlePinnedSave}
              organizations={organizations}
              selectedOrg={selectedOrg}
              onSelectOrg={handleSelectOrg}
            />
          </div>
          <Music className="w-6 h-6 text-slate-300" />
          <p className="text-sm text-slate-400 dark:text-slate-500">No upcoming services scheduled</p>
          {updatedLabel && <p className="text-[11px] text-slate-400 dark:text-slate-500">{updatedLabel}</p>}
          {selectedOrg && (
            <p className="text-xs text-slate-400 dark:text-slate-500">Showing services for {selectedOrg}</p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg shadow-slate-200/50 border border-slate-100 dark:border-white/10 bg-card hover:shadow-xl hover:shadow-slate-200/60 transition-all duration-300" data-testid="pc-widget">
      <CardContent className="p-5">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div data-tile="pc" className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-md shadow-teal-500/25">
              <Music className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Upcoming Services</h3>
              {(updatedLabel || refreshError) && (
                <p className={`text-[11px] leading-tight ${refreshError ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400 dark:text-slate-500'}`} data-testid="pc-updated-label">
                  {refreshError === 'reconnect' ? (
                    <>Couldn't refresh — <button type="button" onClick={onReconnect} className="underline">reconnect Planning Center</button></>
                  ) : refreshError === 'rate' ? 'Planning Center is rate-limiting — some details may be missing; try again in a minute'
                  : refreshError ? 'Couldn\'t refresh — showing the last loaded services'
                  : updatedLabel}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-0.5">
            {/* Manual refresh (R42) */}
            {refreshButton}

            {/* Team settings */}
            <TeamSettings
              userId={userId}
              pinnedTeamIds={pinnedTeamIds}
              onSave={handlePinnedSave}
              organizations={organizations}
              selectedOrg={selectedOrg}
              onSelectOrg={handleSelectOrg}
            />

            {/* Quick message */}
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => {
                      let groupName = null;
                      if (plan.sort_date) {
                        try {
                          const d = new Date(plan.sort_date);
                          if (!isNaN(d.getTime())) {
                            groupName = `🎵 ${d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })} Service`;
                          }
                        } catch {}
                      }
                      onQuickMessage?.({
                        team_members: plan.team_members,
                        group_name: groupName,
                        plan_url: plan.plan_url,
                        songs: plan.songs,
                        song_count: plan.song_count,
                        date: plan.date,
                        time: plan.time,
                      });
                    }}
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 dark:text-slate-400 hover:text-teal-500 hover:bg-slate-50 dark:hover:bg-white/5 transition-all"
                    data-testid="pc-quick-message-btn"
                  >
                    <Mail className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-slate-800 text-white px-3 py-1.5 rounded-lg text-xs font-medium">
                  <p>Quick Message</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Navigation */}
            {totalPlans > 1 && (
              <div className="flex items-center gap-0.5">
                {!isFirst && (
                  <button
                    onClick={() => setCurrentIndex(i => i - 1)}
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 transition-all"
                    data-testid="pc-prev-btn"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                )}
                {isLast ? (
                  <TooltipProvider delayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <a
                          href={PC_SCHEDULE_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 dark:text-slate-400 hover:text-teal-500 hover:bg-slate-50 dark:hover:bg-white/5 transition-all"
                          data-testid="pc-schedule-link"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="bg-slate-800 text-white px-3 py-1.5 rounded-lg text-xs font-medium">
                        <p>View Full Schedule</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <button
                    onClick={() => setCurrentIndex(i => i + 1)}
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 transition-all"
                    data-testid="pc-next-btn"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Date + time */}
        <a href={plan.plan_url} target="_blank" rel="noopener noreferrer" className="block group">
          <div className="flex items-center flex-wrap gap-2 mt-3 mb-3">
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 group-hover:text-teal-600 transition-colors">
              {plan.date}{plan.time ? ` \u00B7 ${plan.time}` : ''}
            </p>
            {plan.my_status === 'not_scheduled' && (
              <span
                className="inline-flex items-center text-[11px] font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 border border-amber-100 dark:border-amber-500/20 rounded-full px-2 py-0.5"
                data-testid="pc-not-scheduled-pill"
              >
                You're not on this week
              </span>
            )}
          </div>

          {/* Team members — 2-column grid */}
          {plan.team_members && plan.team_members.length > 0 && (
            <div className="grid grid-cols-2 gap-1.5 mb-3">
              {plan.team_members.map((m, i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-50/80 dark:bg-white/[0.03] rounded-lg px-2.5 py-1.5">
                  <StatusIcon status={m.status} />
                  <span className="text-sm text-slate-700 dark:text-slate-200 truncate">{formatName(m.name)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Songs */}
          {plan.songs && plan.songs.length > 0 && (
            <div className="border-t border-slate-100 dark:border-white/10 pt-3">
              <div className="flex items-center gap-1.5 mb-2.5">
                <Music className="w-3.5 h-3.5 text-teal-400" />
                <span className="text-sm text-slate-400 dark:text-slate-500">{plan.song_count} songs</span>
              </div>
              <div className="space-y-1.5">
                {plan.songs.map((s, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-slate-700 dark:text-slate-200">{s.title}</span>
                    {s.key && <span className="text-sm text-slate-400 dark:text-slate-500 ml-2 flex-shrink-0">{s.key}</span>}
                  </div>
                ))}
                {plan.song_count > plan.songs.length && (
                  <p className="text-xs text-slate-400 dark:text-slate-500">+{plan.song_count - plan.songs.length} more</p>
                )}
              </div>
            </div>
          )}
        </a>

        {/* Dot indicators */}
        {totalPlans > 1 && (
          <div className="flex items-center justify-center gap-1.5 mt-4 pt-3 border-t border-slate-100 dark:border-white/10">
            {schedules.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                aria-label={`Plan ${i + 1} of ${totalPlans}`}
                className={`rounded-full transition-all ${
                  i === currentIndex ? 'w-5 h-1.5 bg-teal-500' : 'w-1.5 h-1.5 bg-slate-200 dark:bg-[#3e4145] hover:bg-slate-300'
                }`}
                data-testid={`pc-dot-${i}`}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
